import { Form, Link, useMatches } from '@remix-run/react'
import React from 'react'
import { Icon, type IconName } from '../ui/icons/icon.tsx'
import { useOptionalUser } from '~/features/auth/user.ts'
import {
  Accordion,
  AccordionItem,
  AccordionContent,
  AccordionTrigger,
} from '~/components/ui/accordion.tsx'
import { cn } from '~/utils/misc.tsx'
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from '../ui/hover-card.tsx'
import { Ripple } from '../ui/ripple/Ripple.tsx'
import { CompanyTypeEnum, RoleEnum } from 'types/enums.ts'

export interface DrawerItem {
  title: React.ReactNode
  linkClassName?: string
  icon?: React.ReactNode
  href?: string
  children?: DrawerChildItem[]
  hrefMatch?: string
  isExactPath?: boolean
}

export interface DrawerChildItem extends Omit<DrawerItem, 'icon'> {
  icon?: { name: IconName; className?: string }
}

interface LeftDrawerNavigatorProps {
  items: DrawerItem[]
  isOpen: boolean | undefined
  onClose: () => void
  isMobile: boolean
}

const DrawerNavigator = ({
  items,
  isOpen,
  onClose,
  isMobile,
}: LeftDrawerNavigatorProps) => {
  const matches = useMatches()
  const pathName = matches[matches.length - 1].pathname
  const user = useOptionalUser()

  const isDrawerOpen = isOpen ?? !isMobile

  const isOpenStyles = () => {
    return isDrawerOpen
      ? `!w-[350px] !max-w-full ${
          user?.role === RoleEnum.USER &&
          user?.company?.type === CompanyTypeEnum.SHIPPER
            ? 'md:!w-[257px]'
            : 'md:!w-[230px]'
        } `
      : `!w-[0px]  ${
          user?.role === RoleEnum.USER &&
          user?.company?.type === CompanyTypeEnum.SHIPPER
            ? 'md:!w-[257px]'
            : 'md:!w-[230px]'
        } invisible md:visible`
  }

  const getAccordionDefaultValue = () => {
    const item = items.find(item => isActiveLink(pathName, item))
    return item ? [item.title] : []
  }

  return (
    <div className={isMobile ? 'md:hidden' : 'hidden md:block'}>
      <aside
        className={`fixed inset-0 flex h-full w-[0] bg-primary-100 transition-all duration-100 md:sticky md:top-[80px] md:h-[calc(100vh-90px)]
       ${
         user?.role === RoleEnum.USER &&
         user?.company?.type === CompanyTypeEnum.SHIPPER
           ? 'md:w-[257px]'
           : 'md:w-[230px]'
       } md:bg-transparent md:shadow-none ${isOpenStyles()} ${
         isMobile && 'z-[100]'
       } `}
      >
        <div className="flex w-full flex-col justify-between">
          <div className="flex justify-between p-5 md:hidden">
            <img
              className="h-[38px] max-w-none md:w-full md:object-contain"
              src="/img/logo-itraack.svg"
              alt="ITrack logo"
            />
            <Icon
              name="close"
              size="drawer"
              className="cursor-pointer text-foreground md:hidden"
              onClick={onClose}
            />
          </div>
          <nav className="flex flex-1 flex-col bg-primary-100 p-3 md:rounded-[2rem]">
            <div className="flex flex-1 flex-col space-y-2">
              {isDrawerOpen ? (
                <Accordion
                  type="multiple"
                  className="flex flex-1 flex-col space-y-1"
                  defaultValue={getAccordionDefaultValue() as string[]}
                >
                  {items.map((item, index) =>
                    !item.href ? (
                      // Section title
                      <div
                        key={item.title as string}
                        className="pl-2 text-sm font-bold text-gray-400"
                      >
                        {item.title}
                      </div>
                    ) : item.children ? (
                      // Accordion
                      <AccordionItem
                        value={item.title as string}
                        key={item.title as string}
                      >
                        <DrawerDropdownTrigger
                          item={item}
                          pathName={pathName}
                        />
                        <AccordionContent>
                          {item.children.map((child, index) => (
                            <DrawerDropdownItem
                              isDrawerOpen={isDrawerOpen}
                              item={child}
                              pathName={pathName}
                              key={index}
                              onNavigate={() => (isMobile ? onClose() : null)}
                            />
                          ))}
                        </AccordionContent>
                      </AccordionItem>
                    ) : (
                      // Link
                      <DrawerLink
                        item={item}
                        pathName={pathName}
                        key={index}
                        onNavigate={() => (isMobile ? onClose() : null)}
                        linkClassName={item.linkClassName}
                      />
                    ),
                  )}
                </Accordion>
              ) : (
                // Open false
                <>
                  {items.map((item, index) =>
                    item.children ? (
                      <HoverCard
                        openDelay={150}
                        closeDelay={150}
                        key={item.title as string}
                      >
                        <DrawerHoverCardTrigger
                          pathName={pathName}
                          item={item}
                        />
                        <HoverCardContent
                          side="right"
                          className="-ml-1 w-[200px] p-1"
                        >
                          {item.children.map((child, index) => (
                            <DrawerDropdownItem
                              isDrawerOpen={isDrawerOpen}
                              item={child}
                              pathName={pathName}
                              key={index}
                              onNavigate={() => (isMobile ? onClose() : null)}
                            />
                          ))}
                        </HoverCardContent>
                      </HoverCard>
                    ) : (
                      <DrawerLink
                        item={item}
                        pathName={pathName}
                        key={index}
                        onNavigate={() => (isMobile ? onClose() : null)}
                        linkClassName={item.linkClassName}
                      />
                    ),
                  )}
                </>
              )}
            </div>
            {user && (
              <div className="flex flex-1 flex-col justify-end space-y-2">
                <Form
                  action="/logout"
                  method="POST"
                  className={`rounded-full text-foreground-not-selected hover:bg-background hover:text-black`}
                >
                  <button
                    type="submit"
                    className={`flex w-full cursor-pointer items-center justify-start space-x-5 overflow-hidden p-3 [&>svg]:text-primary`}
                  >
                    <Icon name="logout" size="drawer" />
                    <div className={`ml-1 whitespace-nowrap `}>
                      Cerrar sesión
                    </div>
                  </button>
                </Form>
              </div>
            )}
          </nav>
        </div>
      </aside>
      {isDrawerOpen && (
        <div
          className={`fixed inset-0 z-50 h-full w-full bg-overlay md:hidden`}
          onClick={onClose}
        />
      )}
    </div>
  )
}

const isActiveLink = (pathName: string, item: DrawerItem | DrawerChildItem) => {
  if (!item.href) return false
  const href = item.hrefMatch ?? item.href
  if (item.isExactPath) {
    return pathName === href
  }
  return pathName.includes(href)
}
const DrawerLink = ({
  pathName,
  item,
  onNavigate,
  linkClassName,
}: {
  pathName: string
  item: DrawerItem
  onNavigate: () => void
  linkClassName: string | undefined
}) =>
  item.href ? (
    <Link
      to={item.href!}
      key={item.title as string}
      onClick={onNavigate}
      className={cn(
        linkClassName,
        `relative overflow-hidden rounded-full text-foreground-not-selected hover:text-foreground-not-selected  ${
          isActiveLink(pathName, item)
            ? 'bg-white font-semibold !text-primary  hover:bg-white [&[data-state=open]]:bg-white'
            : ''
        }`,
      )}
    >
      <div
        className={`flex cursor-pointer items-center justify-start space-x-5 px-5 py-3`}
      >
        {item.icon}
        <span className={`overflow-hidden whitespace-nowrap text-sm`}>
          {item.title}
        </span>
      </div>
      <Ripple
        className={
          isActiveLink(pathName, item) ? 'bg-primary-500' : 'bg-gray-400'
        }
      />
    </Link>
  ) : null

const DrawerDropdownTrigger = ({
  pathName,
  item,
}: {
  pathName: string
  item: DrawerItem
}) => {
  return (
    <AccordionTrigger
      className={cn(
        'relative mb-1 space-x-5 overflow-hidden rounded-full px-5 py-3 pr-3 text-foreground-not-selected hover:bg-gray-100 hover:no-underline [&[data-state=open]]:bg-gray-100 [&[data-state=open]]:text-foreground',
        isActiveLink(pathName, item) &&
          'bg-white font-bold hover:bg-white [&[data-state=open]]:bg-white',
      )}
    >
      <div className={`flex items-center justify-start space-x-5 `}>
        {item.icon}
        <span className={` whitespace-nowrap`}>{item.title}</span>
      </div>
      <Ripple
        className={
          isActiveLink(pathName, item) ? 'bg-primary-500' : 'bg-gray-400'
        }
      />
    </AccordionTrigger>
  )
}

const DrawerHoverCardTrigger = ({
  pathName,
  item,
}: {
  pathName: string
  item: DrawerItem
}) => {
  return (
    <HoverCardTrigger
      className={cn(
        item.linkClassName,
        'mb-1 space-x-5 overflow-hidden rounded-full px-5 py-3 pr-1 text-foreground-not-selected hover:bg-gray-100 hover:no-underline [&[data-state=open]]:bg-gray-100  [&[data-state=open]]:text-foreground',
        isActiveLink(pathName, item) &&
          'bg-primary font-bold !text-secondary hover:bg-primary [&[data-state=open]]:bg-primary',
      )}
    >
      <div className={`flex items-center justify-start gap-1`}>
        {/* <div className='scale-50'> */}
        {item.icon}
        {/* </div> */}
        <Icon size="font" name="chevron-right" />
      </div>
    </HoverCardTrigger>
  )
}

const DrawerDropdownItem = ({
  pathName,
  item,
  onNavigate,
  isDrawerOpen,
}: {
  pathName: string
  item: DrawerChildItem
  onNavigate: () => void
  isDrawerOpen: boolean
}) => {
  const isActive = isActiveLink(pathName, item)
  return (
    <Link
      to={item.href!}
      key={item.title as string}
      onClick={onNavigate}
      className={` text-foreground-not-selected transition-all hover:bg-gray-100 hover:text-foreground-not-selected`}
    >
      <div
        className={`relative flex cursor-pointer items-center justify-start space-x-5 overflow-hidden  rounded-full px-5 py-2 hover:bg-gray-100 ${
          isActive && 'font-semibold text-foreground'
        }`}
      >
        {isDrawerOpen && (
          <>
            {item.icon ? (
              <Icon
                name={item.icon.name}
                size="drawer"
                className={cn(
                  item.icon.className,
                  isActive && 'text-secondary',
                )}
              />
            ) : (
              <Icon
                size="drawer"
                className={cn(
                  '-ml-[4px]',
                  isActive && 'scale-[2.0] text-secondary',
                )}
                name="dot"
              />
            )}
          </>
        )}
        <span className={`overflow-hidden whitespace-nowrap`}>
          {item.title}
        </span>
        <Ripple className={'bg-gray-400'} />
      </div>
    </Link>
  )
}

export default DrawerNavigator
